// InsightEditor.js
import React, { useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Collapse,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { styled } from "@mui/material/styles";
import { AppButton } from "./ui";
import { useAuth } from "../context/AuthContext";
import { isEducatorRole } from "../utility/accountRoles";
import { useContent } from "../context/ContentContext";

// Styled IconButton that rotates when expanded
const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

// Enum options based on your C# enums

// For InsightType (C# enum: Instruction, Information, Assessment)
const insightTypeOptions = [
  { value: 0, label: "Instruction" },
  { value: 1, label: "Information" },
  { value: 2, label: "Assessment" },
];

// For InsightTriggerType (C# enum: Auto, EnvironmentVisited, InsightComplete, ConditionTriggered, MenuLoaded)
const triggerTypeOptions = [
  { value: 0, label: "Auto" },
  { value: 1, label: "EnvironmentVisited" },
  { value: 2, label: "InsightComplete" },
  { value: 3, label: "ConditionTriggered" },
  { value: 4, label: "MenuLoaded" },
];

// For InsightConditionType (C# enum)
const conditionTypeOptions = [
  { value: 0, label: "EnvironmentsVisited" },
  { value: 1, label: "EnvironmentsNotVisited" },
  { value: 2, label: "ElementsExpanded" },
  { value: 3, label: "ElementsNotExpanded" },
  { value: 4, label: "InsightsCompleted" },
  { value: 5, label: "InsightsNotCompleted" },
  { value: 6, label: "ConditionsTriggered" },
  { value: 7, label: "ConditionsNotTriggered" },
  { value: 8, label: "PlayerPrefsHasKey" },
  { value: 9, label: "PlayerPrefsNotHasKey" },
  { value: 10, label: "TimeSpent" },
];

const insightTypeMarkerMap = {
  0: {
    kind: "text",
    value: "i",
    color: "#ef6c00",
    textSx: {
      fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
      fontStyle: "italic",
      fontWeight: 700,
      fontSize: "1.1em",
      transform: "translateY(-0.02em)",
    },
  },
  1: { kind: "icon", value: MenuBookOutlinedIcon, color: "#1565c0" },
  2: { kind: "text", value: "?", color: "#2e7d32" },
};

const isEnvironmentConditionType = (type) => [0, 1].includes(Number(type));
const isElementConditionType = (type) => [2, 3].includes(Number(type));
const isInsightConditionType = (type) => [4, 5].includes(Number(type));
const isTriggeredConditionType = (type) => [6, 7].includes(Number(type));
const isPlayerPrefConditionType = (type) => [8, 9].includes(Number(type));

const getSingleConditionValue = (value) => {
  if (Array.isArray(value)) {
    return value[0] || "";
  }

  return value || "";
};

const getPipeSeparatedValue = (value) => {
  if (Array.isArray(value)) {
    return value.join("|");
  }

  return value || "";
};

const normalizeInlineText = (value = "") => String(value).replace(/\s+/g, " ").trim();

const truncateText = (value = "", maxLength = 60) => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
};

const tooltipSurfaceProps = {
  tooltip: {
    sx: {
      backgroundColor: "rgba(19, 24, 33, 0.96)",
      color: "#ffffff",
      borderRadius: "var(--radius-md, 8px)",
      px: 1.5,
      py: 1.25,
      fontSize: 12,
      lineHeight: 1.45,
      maxWidth: 280,
      boxShadow: "0 10px 24px rgba(0, 0, 0, 0.24)",
    },
  },
  arrow: {
    sx: {
      color: "rgba(19, 24, 33, 0.96)",
    },
  },
};

const InfoTooltipIcon = ({ title, sx }) => (
  <Tooltip title={title} arrow enterTouchDelay={0} componentsProps={tooltipSurfaceProps}>
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        cursor: "help",
        lineHeight: 1,
        ...sx,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </Box>
  </Tooltip>
);

const InsightEditor = ({
  insight,
  index,
  onChange,
  onDelete,
  onClone,
  allInsights,
  environments,
  elements,
  expandRequest,
}) => {

  const [expanded, setExpanded] = useState(false);
  const { roleName } = useAuth();
  const { getThumbnail } = useContent();
  const [environmentThumbnailUrl, setEnvironmentThumbnailUrl] = useState("");
  const environmentOptions = Array.isArray(environments) ? environments : [];
  const elementOptions = Array.isArray(elements) ? elements : [];
  const insightOptions = Array.isArray(allInsights) ? allInsights : [];



  const hasTrigger = (triggerType) =>
    Array.isArray(insight.insightTriggers) &&
    insight.insightTriggers.some((trigger) => trigger.insightTriggerType === triggerType);

  const startConditions = Array.isArray(insight.startConditions) ? insight.startConditions : [];
  const endConditions = Array.isArray(insight.endConditions) ? insight.endConditions : [];
  const showInternalId = !isEducatorRole(roleName);
  const environmentTrigger = Array.isArray(insight.insightTriggers)
    ? insight.insightTriggers.find((trigger) => trigger.insightTriggerType === 1)
    : null;
  const environmentName =
    environmentOptions.find((environment) => environment.id === environmentTrigger?.triggerId)?.name ||
    environmentTrigger?.name ||
    "";
  const hasInsightCompleteTrigger = hasTrigger(2);
  const hasEnvironmentEndCondition = endConditions.some((condition) =>
    [0, 1].includes(Number(condition?.InsightConditionType))
  );
  const hasTriggeredEndCondition = endConditions.some((condition) =>
    [6, 7].includes(Number(condition?.InsightConditionType))
  );
  const normalizedInsightType = Number.isFinite(Number(insight.insightType))
    ? Number(insight.insightType)
    : null;
  const insightTypeMarker = normalizedInsightType !== null
    ? insightTypeMarkerMap[normalizedInsightType]
    : null;
  const style = hasTrigger(1)
    ? {
        backgroundColor: "var(--color-action-primary-soft, rgba(35, 159, 198, 0.12))",
        border: "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.12))",
      }
    : hasTrigger(2)
    ? { marginLeft: "40px" }
    : {};
  const collapsedName = insight.name || "";
  const collapsedScript = insight.text || "";

  useEffect(() => {
    let cancelled = false;

    if (!environmentTrigger?.triggerId) {
      setEnvironmentThumbnailUrl("");
      return () => {
        cancelled = true;
      };
    }

    getThumbnail(environmentTrigger.triggerId)
      .then((url) => {
        if (!cancelled) {
          setEnvironmentThumbnailUrl(url || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnvironmentThumbnailUrl("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [environmentTrigger?.triggerId, getThumbnail]);

  useEffect(() => {
    if (expandRequest?.id === insight.id) {
      setExpanded(true);
    }
  }, [expandRequest, insight.id]);

  

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  


  // A helper that updates the field in the insight object
  const handleFieldChange = (field, value) => {
    // No parsing here – let GuideEditor convert on save.
    onChange(index, { ...insight, [field]: value });
  };


  // ---------------------------
  // Trigger handlers
  // ---------------------------
  const handleTriggerChange = (triggerIndex, field, value) => {
    const newTriggers = insight.insightTriggers
      ? [...insight.insightTriggers]
      : [];
    newTriggers[triggerIndex] = {
      ...newTriggers[triggerIndex],
      [field]: value,
    };
    handleFieldChange("insightTriggers", newTriggers);
  };

  const addTrigger = () => {
    const newTriggers = insight.insightTriggers
      ? [...insight.insightTriggers]
      : [];
    newTriggers.push({ name: "", insightTriggerType: 0, triggerId: "" });
    handleFieldChange("insightTriggers", newTriggers);
  };

  const removeTrigger = (triggerIndex) => {
    const newTriggers = insight.insightTriggers
      ? [...insight.insightTriggers]
      : [];
    newTriggers.splice(triggerIndex, 1);
    handleFieldChange("insightTriggers", newTriggers);
  };

  // ---------------------------
  // Start Conditions handlers
  // ---------------------------
  const handleStartConditionChange = (condIndex, field, value) => {
    const newConds = insight.startConditions ? [...insight.startConditions] : [];
    newConds[condIndex] = { ...newConds[condIndex], [field]: value }; // keep it as a string
    handleFieldChange("startConditions", newConds);
  };


  const addStartCondition = () => {
    const newConds = insight.startConditions
      ? [...insight.startConditions]
      : [];
    newConds.push({
      name: "",
      InsightConditionType: 0,
      conditions: "",
    });
    handleFieldChange("startConditions", newConds);
  };

  const removeStartCondition = (condIndex) => {
    const newConds = insight.startConditions
      ? [...insight.startConditions]
      : [];
    newConds.splice(condIndex, 1);
    handleFieldChange("startConditions", newConds);
  };

  // ---------------------------
  // End Conditions handlers
  // ---------------------------
  const handleEndConditionChange = (condIndex, field, value) => {
    const newConds = insight.endConditions ? [...insight.endConditions] : [];
    newConds[condIndex] = { ...newConds[condIndex], [field]: value }; // keep it as a string
    handleFieldChange("endConditions", newConds);
  };

  const addEndCondition = () => {
    const newConds = insight.endConditions ? [...insight.endConditions] : [];
    newConds.push({
      name: "",
      InsightConditionType: 0,
      conditions: "",
    });
    handleFieldChange("endConditions", newConds);
  };

  const removeEndCondition = (condIndex) => {
    const newConds = insight.endConditions ? [...insight.endConditions] : [];
    newConds.splice(condIndex, 1);
    handleFieldChange("endConditions", newConds);
  };

  const handleTriggerTypeChange = (triggerIndex, nextType) => {
    const newTriggers = insight.insightTriggers ? [...insight.insightTriggers] : [];
    const previousTrigger = newTriggers[triggerIndex] || {};
    const preserveName = nextType === 3;
    const preserveValue = nextType === 3 || nextType === 4;

    newTriggers[triggerIndex] = {
      ...previousTrigger,
      insightTriggerType: nextType,
      name: preserveName ? previousTrigger.name || "" : "",
      triggerId: preserveValue ? previousTrigger.triggerId || "" : "",
    };

    handleFieldChange("insightTriggers", newTriggers);
  };

  const handleConditionTypeChange = (section, condIndex, nextType) => {
    const field = section === "start" ? "startConditions" : "endConditions";
    const currentConditions = section === "start" ? insight.startConditions : insight.endConditions;
    const newConditions = currentConditions ? [...currentConditions] : [];
    const previousCondition = newConditions[condIndex] || {};
    const nextValue =
      isEnvironmentConditionType(nextType) ||
      isElementConditionType(nextType) ||
      isInsightConditionType(nextType)
        ? getSingleConditionValue(previousCondition.conditions)
        : getPipeSeparatedValue(previousCondition.conditions);

    newConditions[condIndex] = {
      ...previousCondition,
      InsightConditionType: nextType,
      conditions: nextValue,
    };

    handleFieldChange(field, newConditions);
  };

  const resolveElementName = (element, elementIndex) =>
    element?.name || element?.labelData?.displayName || `Element ${elementIndex + 1}`;

  const getInsightOptionData = (candidate, candidateIndex) => {
    const shortName = normalizeInlineText(candidate?.name || "");
    const scriptPreview = truncateText(normalizeInlineText(candidate?.text || ""), 56);
    const environmentVisitedTrigger = Array.isArray(candidate?.insightTriggers)
      ? candidate.insightTriggers.find((trigger) => Number(trigger?.insightTriggerType) === 1)
      : null;
    const environmentLabel =
      environmentOptions.find((environment) => environment.id === environmentVisitedTrigger?.triggerId)?.name ||
      environmentVisitedTrigger?.name ||
      "";
    const primaryLabel = shortName || scriptPreview || "Untitled insight";
    const secondaryParts = [`#${candidateIndex + 1}`];

    if (environmentLabel) {
      secondaryParts.push(environmentLabel);
    }

    return {
      primaryLabel,
      secondaryLabel: secondaryParts.join(" • "),
      insightType: candidate?.insightType,
    };
  };

  const renderInsightOptionContent = (candidate, candidateIndex) => {
    const { primaryLabel, secondaryLabel, insightType } = getInsightOptionData(candidate, candidateIndex);

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0, py: 0.25 }}>
        {renderInsightTypeMarker(insightType, 18)}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {primaryLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {secondaryLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderInsightSelectValue = (selectedValue) => {
    const selectedIndex = insightOptions.findIndex((candidate) => (candidate.id || "") === selectedValue);
    const selectedInsight = selectedIndex >= 0 ? insightOptions[selectedIndex] : null;

    if (!selectedInsight) {
      return "";
    }

    const { primaryLabel, secondaryLabel, insightType } = getInsightOptionData(selectedInsight, selectedIndex);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        {renderInsightTypeMarker(insightType, 16)}
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {primaryLabel}
          </Box>
          <Box component="span" sx={{ color: "text.secondary" }}>
            {secondaryLabel ? ` • ${secondaryLabel}` : ""}
          </Box>
        </Typography>
      </Box>
    );
  };

  const renderTriggerDetails = (trigger, idx) => {
    if (trigger.insightTriggerType === 1) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Environment</InputLabel>
          <Select
            label="Environment"
            value={trigger.triggerId || ""}
            onChange={(e) => {
              const selectedId = e.target.value;
              const selectedEnv = environmentOptions.find((env) => env.id === selectedId);
              const newTriggers = insight.insightTriggers ? [...insight.insightTriggers] : [];
              newTriggers[idx] = {
                ...newTriggers[idx],
                triggerId: selectedId,
                name: selectedEnv ? selectedEnv.name : "",
              };
              handleFieldChange("insightTriggers", newTriggers);
            }}
          >
            {environmentOptions.map((env) => (
              <MenuItem key={env.id} value={env.id}>
                {env.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (trigger.insightTriggerType === 2) {
      return (
        <TextField
          label="Previous Insight"
          value={trigger.name || "Previous Insight"}
          fullWidth
          size="small"
          disabled
        />
      );
    }

    if (trigger.insightTriggerType === 3) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 180px) minmax(0, 1fr)" },
            gap: 1,
          }}
        >
          <TextField
            label="Condition"
            value={trigger.name || ""}
            onChange={(e) => handleTriggerChange(idx, "name", e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Trigger Value"
            value={trigger.triggerId || ""}
            onChange={(e) => handleTriggerChange(idx, "triggerId", e.target.value)}
            fullWidth
            size="small"
          />
        </Box>
      );
    }

    if (trigger.insightTriggerType === 4) {
      return (
        <TextField
          label="Menu Value"
          value={trigger.triggerId || ""}
          onChange={(e) => handleTriggerChange(idx, "triggerId", e.target.value)}
          fullWidth
          size="small"
        />
      );
    }

    return null;
  };

  const renderConditionTargetField = (cond, idx, section) => {
    const onChange =
      section === "start"
        ? handleStartConditionChange
        : handleEndConditionChange;
    const type = Number(cond.InsightConditionType);

    if (isEnvironmentConditionType(type)) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Environment</InputLabel>
          <Select
            label="Environment"
            value={getSingleConditionValue(cond.conditions)}
            onChange={(e) => onChange(idx, "conditions", e.target.value)}
          >
            {environmentOptions.map((env) => (
              <MenuItem key={env.id} value={env.id}>
                {env.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (isElementConditionType(type)) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Element</InputLabel>
          <Select
            label="Element"
            value={getSingleConditionValue(cond.conditions)}
            onChange={(e) => onChange(idx, "conditions", e.target.value)}
          >
            {elementOptions.map((element, elementIndex) => (
              <MenuItem key={element.id} value={element.id}>
                {resolveElementName(element, elementIndex)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (isInsightConditionType(type)) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Insight</InputLabel>
          <Select
            label="Insight"
            value={getSingleConditionValue(cond.conditions)}
            onChange={(e) => onChange(idx, "conditions", e.target.value)}
            renderValue={renderInsightSelectValue}
          >
            {insightOptions.map((candidate, candidateIndex) => (
              <MenuItem key={candidate.id || candidateIndex} value={candidate.id || ""}>
                {renderInsightOptionContent(candidate, candidateIndex)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (isTriggeredConditionType(type)) {
      return (
        <TextField
          label="Condition Ids"
          value={getPipeSeparatedValue(cond.conditions)}
          onChange={(e) => onChange(idx, "conditions", e.target.value)}
          fullWidth
          size="small"
        />
      );
    }

    if (isPlayerPrefConditionType(type)) {
      return (
        <TextField
          label="Player Pref Key"
          value={getPipeSeparatedValue(cond.conditions)}
          onChange={(e) => onChange(idx, "conditions", e.target.value)}
          fullWidth
          size="small"
        />
      );
    }

    return (
      <TextField
        label={type === 10 ? "Seconds" : "Value"}
        value={getPipeSeparatedValue(cond.conditions)}
        onChange={(e) => onChange(idx, "conditions", e.target.value)}
        fullWidth
        size="small"
      />
    );
  };

  const renderConditionRows = (section, conditions) => {
    const isEndSection = section === "end";
    const handleChange =
      section === "start"
        ? handleStartConditionChange
        : handleEndConditionChange;
    const removeCondition =
      section === "start"
        ? removeStartCondition
        : removeEndCondition;

    return (
      <Box sx={{ display: "grid" }}>
        {conditions.map((cond, idx) => (
          <Box
            key={`${section}-${idx}`}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr) auto",
                md: "minmax(220px, 1.2fr) minmax(180px, 220px) minmax(0, 1fr) auto",
              },
              gap: 1,
              alignItems: "start",
              py: 1,
              borderBottom:
                idx === conditions.length - 1
                  ? "none"
                  : "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08))",
            }}
          >
            <TextField
              label={isEndSection ? "User-facing Instructions" : "Condition Name"}
              value={cond.name || ""}
              onChange={(e) => handleChange(idx, "name", e.target.value)}
              fullWidth
              size="small"
              sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={cond.InsightConditionType !== undefined ? cond.InsightConditionType : ""}
                onChange={(e) => handleConditionTypeChange(section, idx, parseInt(e.target.value, 10))}
              >
                {conditionTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0 }}>
              {renderConditionTargetField(cond, idx, section)}
            </Box>

            <IconButton
              size="small"
              onClick={() => removeCondition(idx)}
              aria-label={isEndSection ? "Remove end condition" : "Remove start condition"}
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  const renderInsightTypeMarker = (insightType, size = 26) => {
    const marker =
      Number.isFinite(Number(insightType)) && insightTypeMarkerMap[Number(insightType)]
        ? insightTypeMarkerMap[Number(insightType)]
        : null;

    if (!marker) {
      return null;
    }

    const MarkerIcon = marker.kind === "icon" ? marker.value : null;

    return (
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          backgroundColor: marker.color,
          color: "#ffffff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: Math.round(size * 0.58),
          fontWeight: 700,
          lineHeight: 1,
          textTransform: "none",
          flexShrink: 0,
        }}
      >
        {MarkerIcon ? (
          <MarkerIcon fontSize="inherit" />
        ) : (
          <Box component="span" sx={marker.textSx}>
            {marker.value}
          </Box>
        )}
      </Box>
    );
  };

  const sxIndent = { ml: 8 };

  return (
    <Card key={insight.id} sx={style}>
      {/* Card Header: Editable Name field and Expand/Collapse button */}
      {/* {environments.map((env, i) => (
      <p>{env.name}</p>
    ))} */}
      <CardActions
        sx={{
          flexDirection: "column",
          alignItems: "stretch",
          padding: 1,
          paddingBottom: expanded ? 0.25 : 1,
        }}
      >
        {/* Header Row */}
        {expanded ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
            }}
          >
            <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
              <ExpandMoreIcon fontSize="small" />
            </ExpandMore>
            <Box sx={{ flex: 1 }} />
            {showInternalId && (
              <Typography variant="caption" sx={{ ml: 1 }}>
                {insight.id}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1,
              minWidth: 0,
            }}
          >
            <ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
              <ExpandMoreIcon fontSize="small" />
            </ExpandMore>

            {environmentTrigger ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                {environmentThumbnailUrl ? (
                  <Box
                    component="img"
                    src={environmentThumbnailUrl}
                    alt={environmentName ? `${environmentName} thumbnail` : "Environment thumbnail"}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                {environmentName ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      color: "text.primary",
                    }}
                  >
                    {environmentName}
                  </Typography>
                ) : null}
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  |
                </Typography>
              </Box>
            ) : hasInsightCompleteTrigger ? (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ↳
              </Typography>
            ) : null}

            {insightTypeMarker ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                {renderInsightTypeMarker(normalizedInsightType, 26)}
              </Box>
            ) : null}

            <Typography
              variant="body2"
              component="div"
              sx={{
                minWidth: 0,
                flex: 1,
                color: "text.primary",
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                lineHeight: 1.35,
              }}
            >
              {collapsedName || collapsedScript ? (
                <>
                  {collapsedName ? (
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {collapsedName}
                    </Box>
                  ) : null}
                  {collapsedScript ? (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {collapsedName ? " : " : ""}
                      {collapsedScript}
                    </Box>
                  ) : null}
                </>
              ) : (
                "Untitled insight"
              )}
            </Typography>

            {(hasEnvironmentEndCondition || hasTriggeredEndCondition) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                  ml: 1,
                }}
              >
                {hasEnvironmentEndCondition ? (
                  <Box
                    component="span"
                    title="End condition: environment visited"
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: "var(--color-state-info-bg, rgba(56, 189, 248, 0.15))",
                      border: "1px solid var(--color-state-info-border, rgba(56, 189, 248, 0.3))",
                      color: "var(--color-action-info, var(--color-action-primary, #239fc6))",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                ) : null}

                {hasTriggeredEndCondition ? (
                  <Box
                    component="span"
                    title="End condition: condition triggered"
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: "var(--color-action-warning-soft, rgba(247, 195, 95, 0.22))",
                      border: "1px solid var(--color-state-warning-border, rgba(251, 191, 36, 0.3))",
                      color: "var(--color-action-warning, #f7c35f)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BoltOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        )}
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ ...sxIndent, pt: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(220px, 1fr) minmax(0, 2fr)",
              },
              gap: 2,
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                alignContent: "start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 0.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  label="Short Name"
                  value={insight.name || ""}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <InfoTooltipIcon
                  title="A short label used to identify this insight in the editor and in insight pickers."
                  sx={{ mt: 1.1 }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr) auto",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                {renderInsightTypeMarker(normalizedInsightType, 30)}
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={insight.insightType !== undefined ? insight.insightType : ""}
                    onChange={(e) => handleFieldChange("insightType", parseInt(e.target.value, 10))}
                  >
                    {insightTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <InfoTooltipIcon
                  title="Instruction tells the learner what to do, Information explains or teaches, and Assessment checks understanding and scores the learner's response."
                  sx={{ mt: 0.25 }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  opacity: 0.65,
                  pt: 0.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={insight.dismissable || false}
                      onChange={(e) => handleFieldChange("dismissable", e.target.checked)}
                    />
                  }
                  label="Dismissable"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 12,
                      color: "text.secondary",
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                alignContent: "start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 0.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  label="Script"
                  value={insight.text || ""}
                  onChange={(e) => handleFieldChange("text", e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                />
                <InfoTooltipIcon
                  title="This text is automatically turned into audio voiceover. Changing it will generate a new voiceover."
                  sx={{ mt: 1.1 }}
                />
              </Box>

              {insight.insightType === 2 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 0.5,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    label="Answer Key"
                    value={insight.answerKey || ""}
                    onChange={(e) => handleFieldChange("answerKey", e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                  />
                  <InfoTooltipIcon
                    title="What we compare the student's verbal answer against to generate a formative score."
                    sx={{ mt: 1.1 }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ marginBottom: 1.5 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Triggers
              </Typography>
              <InfoTooltipIcon title="What causes this insight to be played." />
            </Box>
            <AppButton
              kind="ghost"
              size="small"
              startIcon={<AddCircleOutlineIcon fontSize="small" />}
              onClick={addTrigger}
            >
              Add Trigger
            </AppButton>
          </Box>

          <Box sx={{ display: "grid", mb: 1.5 }}>
            {insight.insightTriggers &&
              insight.insightTriggers.map((trigger, idx) => (
                <Box
                  key={`trigger-${idx}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr) auto",
                      md: "minmax(180px, 220px) minmax(0, 1fr) auto",
                    },
                    gap: 1,
                    alignItems: "start",
                    py: 1,
                    borderBottom:
                      idx === insight.insightTriggers.length - 1
                        ? "none"
                        : "1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08))",
                  }}
                >
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={trigger.insightTriggerType !== undefined ? trigger.insightTriggerType : 0}
                      onChange={(e) => handleTriggerTypeChange(idx, parseInt(e.target.value, 10))}
                    >
                      {triggerTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0 }}>
                    {renderTriggerDetails(trigger, idx)}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => removeTrigger(idx)}
                    aria-label="Remove trigger"
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
          </Box>

          <Divider sx={{ marginBottom: 1.5 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Conditions
              </Typography>
              <InfoTooltipIcon title="Extra rules that control when this insight can start or when it should end." />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              <AppButton
                kind="ghost"
                size="small"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={addStartCondition}
              >
                Add Start
              </AppButton>
              <AppButton
                kind="ghost"
                size="small"
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={addEndCondition}
              >
                Add End
              </AppButton>
            </Box>
          </Box>

          {startConditions.length > 0 && (
            <Box sx={{ mb: endConditions.length > 0 ? 1.5 : 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.25,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                }}
              >
                Start Conditions
              </Typography>
              {renderConditionRows("start", startConditions)}
            </Box>
          )}

          {endConditions.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.25,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                }}
              >
                End Conditions
              </Typography>
              {renderConditionRows("end", endConditions)}
            </Box>
          )}
        </CardContent>

        <CardActions>
          <AppButton kind="secondary" onClick={() => onClone(index)} sx={{ mr: 1 }}>
            Clone
          </AppButton>
          <AppButton kind="danger" onClick={() => onDelete(index)}>
            Delete
          </AppButton>
        </CardActions>
      </Collapse>
    </Card>
  );
};

export default InsightEditor;
